/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { InputAction } from "../enums/InputAction";

/**
 * Pure grid focus arithmetic — shared by every screen that renders a
 * `ContentGrid`.
 *
 * Screens own D-pad input (`ScreenController`) and focus state (Redux); this
 * module owns only the index math so that no screen has to re-derive
 * "which cell does NAV_DOWN land on when the last row is half full".
 *
 * Framework-free by design: no React, no Redux, no DOM.
 */

/** Direction of a focus move within a grid. */
export const GridDirection = {
  UP: "up",
  DOWN: "down",
  LEFT: "left",
  RIGHT: "right"
} as const;

export type TGridDirection = (typeof GridDirection)[keyof typeof GridDirection];

/**
 * What happens when a focus move runs off the edge of the grid.
 *
 * - `CLAMP` — focus stays where it is (grid swallows the input).
 * - `WRAP`  — focus continues from the opposite edge.
 * - `EXIT`  — `moveGridFocus` returns `null` so the caller can hand focus to
 *             a neighbouring region (filter strip, side nav, tab bar…).
 */
export const GridEdgeBehaviour = {
  CLAMP: "clamp",
  WRAP: "wrap",
  EXIT: "exit"
} as const;

export type TGridEdgeBehaviour =
  (typeof GridEdgeBehaviour)[keyof typeof GridEdgeBehaviour];

/** Row/column coordinate pair within a grid. */
export type TGridPosition = {
  row: number;
  column: number;
};

export type TGridNavigationOptions = {
  /** Total number of focusable items in the grid. */
  itemCount: number;
  /** Number of columns per row (the grid's "max columns"). */
  columns: number;
  /** Edge behaviour for NAV_LEFT / NAV_RIGHT. Defaults to `CLAMP`. */
  horizontalEdge?: TGridEdgeBehaviour;
  /** Edge behaviour for NAV_UP / NAV_DOWN. Defaults to `CLAMP`. */
  verticalEdge?: TGridEdgeBehaviour;
};

/** Number of rows needed to lay out `itemCount` items in `columns` columns. */
export const gridRowCount = (itemCount: number, columns: number): number => {
  if (itemCount <= 0 || columns <= 0) {
    return 0;
  }
  return Math.ceil(itemCount / columns);
};

/** Converts a flat item index into its row/column coordinate. */
export const toGridPosition = (
  index: number,
  columns: number
): TGridPosition => {
  if (columns <= 0 || index < 0) {
    return { row: 0, column: 0 };
  }
  return {
    row: Math.floor(index / columns),
    column: index % columns
  };
};

/** Converts a row/column coordinate back into a flat item index. */
export const toGridIndex = (
  position: TGridPosition,
  columns: number
): number => {
  if (columns <= 0) {
    return 0;
  }
  return position.row * columns + position.column;
};

/** Index of the first item on `row`. */
const rowStartIndex = (row: number, columns: number): number => row * columns;

/** Index of the last populated item on `row`. */
const rowEndIndex = (row: number, columns: number, itemCount: number): number =>
  Math.min(rowStartIndex(row, columns) + columns - 1, itemCount - 1);

/**
 * Resolves a horizontal move that has run past the start/end of its row.
 * Returns the next index, or `null` when focus should leave the grid.
 */
const resolveHorizontalEdge = (
  currentIndex: number,
  row: number,
  behaviour: TGridEdgeBehaviour,
  options: { columns: number; itemCount: number },
  isMovingLeft: boolean
): number | null => {
  if (behaviour === GridEdgeBehaviour.EXIT) {
    return null;
  }
  if (behaviour === GridEdgeBehaviour.WRAP) {
    return isMovingLeft
      ? rowEndIndex(row, options.columns, options.itemCount)
      : rowStartIndex(row, options.columns);
  }
  return currentIndex;
};

/**
 * Resolves a vertical move that has run past the first/last row.
 * Returns the next index, or `null` when focus should leave the grid.
 */
const resolveVerticalEdge = (
  currentIndex: number,
  column: number,
  behaviour: TGridEdgeBehaviour,
  options: { columns: number; itemCount: number; rowCount: number },
  isMovingUp: boolean
): number | null => {
  if (behaviour === GridEdgeBehaviour.EXIT) {
    return null;
  }
  if (behaviour === GridEdgeBehaviour.WRAP) {
    const targetRow: number = isMovingUp ? options.rowCount - 1 : 0;
    const wrapped: number = toGridIndex(
      { row: targetRow, column },
      options.columns
    );
    // The last row can be partially filled — land on its last real item.
    return Math.min(wrapped, options.itemCount - 1);
  }
  return currentIndex;
};

/**
 * Computes the next focused index for a D-pad move inside a grid.
 *
 * @returns the next flat index, or `null` when focus should leave the grid
 *          (edge behaviour `EXIT`, or an empty/misconfigured grid).
 *
 * Behaviour worth knowing:
 * - `currentIndex` is clamped into range first, so a stale index from a
 *   shrunken data set can never focus a non-existent cell.
 * - Moving `DOWN` from the second-to-last row into a partially filled last
 *   row lands on that row's last item instead of dead-ending.
 */
export const moveGridFocus = (
  currentIndex: number,
  direction: TGridDirection,
  options: TGridNavigationOptions
): number | null => {
  const { itemCount, columns } = options;
  const rowCount: number = gridRowCount(itemCount, columns);
  if (rowCount === 0) {
    return null;
  }

  const horizontalEdge: TGridEdgeBehaviour =
    options.horizontalEdge === undefined
      ? GridEdgeBehaviour.CLAMP
      : options.horizontalEdge;
  const verticalEdge: TGridEdgeBehaviour =
    options.verticalEdge === undefined
      ? GridEdgeBehaviour.CLAMP
      : options.verticalEdge;

  const index: number = Math.min(Math.max(currentIndex, 0), itemCount - 1);
  const { row, column } = toGridPosition(index, columns);

  if (direction === GridDirection.LEFT) {
    if (column > 0) {
      return index - 1;
    }
    return resolveHorizontalEdge(index, row, horizontalEdge, options, true);
  }

  if (direction === GridDirection.RIGHT) {
    if (column < columns - 1 && index + 1 < itemCount) {
      return index + 1;
    }
    return resolveHorizontalEdge(index, row, horizontalEdge, options, false);
  }

  if (direction === GridDirection.UP) {
    if (row > 0) {
      return index - columns;
    }
    return resolveVerticalEdge(
      index,
      column,
      verticalEdge,
      {
        columns,
        itemCount,
        rowCount
      },
      true
    );
  }

  // GridDirection.DOWN
  const below: number = index + columns;
  if (below < itemCount) {
    return below;
  }
  if (row < rowCount - 1) {
    // Last row exists but is shorter than `column` — land on its last item.
    return itemCount - 1;
  }
  return resolveVerticalEdge(
    index,
    column,
    verticalEdge,
    {
      columns,
      itemCount,
      rowCount
    },
    false
  );
};

/**
 * Maps a navigation `InputAction` to a grid direction.
 *
 * Lets a `ScreenController` register one shared handler for the four D-pad
 * actions instead of four near-identical ones.
 *
 * @returns the direction, or `null` for any non-directional action.
 */
export const gridDirectionFromInputAction = (
  action: InputAction
): TGridDirection | null => {
  switch (action) {
    case InputAction.NAV_UP:
      return GridDirection.UP;
    case InputAction.NAV_DOWN:
      return GridDirection.DOWN;
    case InputAction.NAV_LEFT:
      return GridDirection.LEFT;
    case InputAction.NAV_RIGHT:
      return GridDirection.RIGHT;
    default:
      return null;
  }
};
